/**
 * Les pièces jointes à « Signaler un problème » : ce que `LiveHelpModal.js` acceptait, et leur envoi.
 *
 * Images et vidéos, comme l'ancien espace : JPEG, PNG, WEBP, MP4, MOV, trois au plus. Le poids : 20 Mo
 * pour une image (l'ancienne modale), 100 Mo pour une vidéo (Wajih, 20/10 : 20 Mo ne faisaient que
 * quelques secondes de vidéo de téléphone). Le serveur, lui, ne borne que le nombre (`attachment_files`,
 * `max:3`, des clés `tmp/…`) : il ne lit ni le type ni le poids, et le stockage accepte un `PUT`
 * présigné jusqu'à 5 Go. Ces limites sont donc celles de l'app ; elles se disent dès le choix de la
 * pièce (`pickedAttachmentRefusal`, avec le type et le poids que le sélecteur connaît), et se
 * revérifient sur le fichier lu avant l'envoi (`attachmentRefusal`).
 *
 * Toutes les pièces sont jugées avant le premier envoi (une pièce refusée ne laisse pas derrière elle
 * une première partie pour rien). Chaque pièce part ensuite au stockage (`POST
 * /vapor/signed-storage-url`, puis le `PUT` présigné, lu : un refus de S3 ne passe plus pour un envoi),
 * **depuis le disque sur mobile** (`issueReportUpload.ts`), et le signalement porte ensuite leurs clés.
 */
import { prepareIssueAttachment } from "./issueReportUpload"

/** Trois pièces au plus : la règle de l'ancienne modale, et celle du serveur (`max:3`). */
export const ISSUE_REPORT_MAX_ATTACHMENTS = 3
const MB = 1024 * 1024
/** 20 Mo pour une image : la limite de l'ancienne modale. */
export const ISSUE_REPORT_MAX_IMAGE_BYTES = 20 * MB
/** 100 Mo pour une vidéo (Wajih, 20/10). Le serveur n'en pose aucune. */
export const ISSUE_REPORT_MAX_VIDEO_BYTES = 100 * MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "video/mp4", "video/quicktime"]

export const ATTACHMENT_TYPE_REFUSED =
    "Type de fichier non supporté : joins une image (JPEG, PNG, WEBP) ou une vidéo (MP4, MOV)."
export const IMAGE_TOO_LARGE = "Image trop lourde : 20 Mo au plus. Choisis-en une autre."
export const VIDEO_TOO_LARGE = "Vidéo trop lourde : 100 Mo au plus. Raccourcis-la ou choisis-en une autre."
export const TOO_MANY_ATTACHMENTS = "Trois pièces au plus : les premières sont gardées."

const isVideoType = (type: string | undefined) => type?.startsWith("video/") === true

/**
 * Le poids qui dépasse, et sa phrase. Un type inconnu (au choix seulement : une pièce sans type) ne se
 * juge qu'à la plus haute des deux limites ; le fichier lu le dira avant l'envoi.
 */
function sizeRefusal(type: string | undefined, size: number | undefined): string | null {
    if (size == null) return null
    if (type == null || type === "") return size > ISSUE_REPORT_MAX_VIDEO_BYTES ? VIDEO_TOO_LARGE : null
    if (isVideoType(type)) return size > ISSUE_REPORT_MAX_VIDEO_BYTES ? VIDEO_TOO_LARGE : null
    return size > ISSUE_REPORT_MAX_IMAGE_BYTES ? IMAGE_TOO_LARGE : null
}
export const REPORT_FAILED = "Ton signalement n'a pas pu partir. Réessaie dans un instant."

/** Une pièce refusée avant l'envoi : sa phrase est celle que l'élève lit. */
export class AttachmentRefusedError extends Error {
    constructor(message: string) {
        super(message)
        this.name = "AttachmentRefusedError"
    }
}

/** L'élève a fermé la feuille pendant l'envoi : le signalement ne part pas. Rien à lui dire. */
export class ReportAbandonedError extends Error {
    constructor() {
        super("Issue report abandoned")
        this.name = "ReportAbandonedError"
    }
}

/** Une pièce telle que le sélecteur la donne : l'adresse, et ce qu'il sait de son type et de son poids. */
export type PickedAttachment = { uri: string; mimeType?: string; fileSize?: number }

export type AttachmentBlob = { type?: string; size?: number }

/**
 * Une pièce prête à partir : son type et son poids connus SANS lire son contenu sur mobile, et l'envoi
 * qui la pose au stockage et rend sa clé.
 */
export type PreparedAttachment = AttachmentBlob & {
    send: (contentType: string) => Promise<{ key?: string } | null | undefined>
}

export type AttachmentDependencies = {
    prepare: (picked: PickedAttachment) => Promise<PreparedAttachment>
}

const defaultDependencies: AttachmentDependencies = { prepare: prepareIssueAttachment }

/** La règle, sur un fichier lu : le format, puis le poids de son genre (image ou vidéo). */
export function attachmentRefusal(blob: AttachmentBlob): string | null {
    if (!ALLOWED_TYPES.includes(blob.type ?? "")) return ATTACHMENT_TYPE_REFUSED
    return sizeRefusal(blob.type, blob.size ?? 0)
}

/**
 * La même règle, dès le choix : seulement sur ce que le sélecteur sait. Un type ou un poids inconnu
 * (une image collée, un ancien appareil) passe, et sera jugé sur le fichier lu avant l'envoi.
 */
export function pickedAttachmentRefusal(picked: PickedAttachment): string | null {
    if (picked.mimeType != null && picked.mimeType !== "" && !ALLOWED_TYPES.includes(picked.mimeType)) {
        return ATTACHMENT_TYPE_REFUSED
    }
    return sizeRefusal(picked.mimeType, picked.fileSize)
}

/**
 * Les pièces choisies, triées : celles qu'on garde (trois au plus) et la phrase de la première refusée.
 * Les pièces déjà là ne sont pas rejugées. Au-delà de trois, les dernières ne sont pas gardées, et la
 * phrase le dit : l'élève ne les perd pas sans le savoir.
 */
export function admitPickedAttachments<T extends PickedAttachment>(
    current: T[],
    next: T[]
): { kept: T[]; refusal: string | null } {
    const known = new Set(current.map((item) => item.uri))
    let refusal: string | null = null
    const kept = next.filter((item) => {
        if (known.has(item.uri)) return true
        const reason = pickedAttachmentRefusal(item)
        refusal ??= reason
        return reason == null
    })
    if (kept.length > ISSUE_REPORT_MAX_ATTACHMENTS) refusal ??= TOO_MANY_ATTACHMENTS
    return { kept: kept.slice(0, ISSUE_REPORT_MAX_ATTACHMENTS), refusal }
}

/**
 * Juge toutes les pièces, puis les envoie une à une et rend leurs clés, dans l'ordre. Une pièce refusée
 * arrête tout avant le premier envoi.
 */
export async function uploadIssueAttachments(
    attachments: PickedAttachment[],
    dependencies: AttachmentDependencies = defaultDependencies,
    /** Relu avant chaque pièce : faux (la feuille fermée), aucune autre ne part. */
    wanted: () => boolean = () => true
): Promise<string[]> {
    const prepared: { item: PreparedAttachment; contentType: string }[] = []
    for (const attachment of attachments.slice(0, ISSUE_REPORT_MAX_ATTACHMENTS)) {
        const item = await dependencies.prepare(attachment)
        // Le type d'un fichier lu sur mobile peut manquer : celui que le sélecteur a donné le remplace.
        const blob = { type: item.type || attachment.mimeType, size: item.size ?? attachment.fileSize }
        const refusal = attachmentRefusal(blob)
        if (refusal != null) throw new AttachmentRefusedError(refusal)
        prepared.push({ item, contentType: blob.type ?? "" })
    }
    const keys: string[] = []
    for (const { item, contentType } of prepared) {
        if (!wanted()) throw new ReportAbandonedError()
        const stored = await item.send(contentType)
        if (stored?.key == null || stored.key === "") throw new Error("S3 upload returned no key")
        keys.push(stored.key)
    }
    return keys
}

/** Ce que l'élève lit quand le signalement n'est pas parti : une pièce refusée dit pourquoi. */
export function issueReportFailureMessage(cause: unknown): string {
    return cause instanceof AttachmentRefusedError ? cause.message : REPORT_FAILED
}
